import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";

import { DEV_HOSTS } from "../constants/hosts";
import {
	dumpFrontendPerf,
	getFrontendPerfSnapshot,
	startFrontendPerfObservers,
	type FrontendPerfSnapshot,
} from "../utils/frontendPerf";
import { dumpOrdersRenderProbe, runOrdersRenderProbe } from "./ordersRenderProbe";
import { dumpOrdersPerfStress, runOrdersPerfStress } from "./ordersPerfHarness";

const Wrapper = styled.div<{ $dragging: boolean }>`
	position: fixed;
	z-index: 9999;
	background: #121212;
	color: #fff;
	padding: 6px 8px;
	border-radius: 8px;
	font-size: 10px;
	font-family: monospace;
	box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
	opacity: 0.95;
	min-width: 248px;
	max-width: 248px;
	line-height: 1.25;
	cursor: ${({ $dragging }) => ($dragging ? "grabbing" : "grab")};
	user-select: none;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: 10px;
	row-gap: 1px;
`;

const Row = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 6px;
	min-width: 0;
`;

const Value = styled.b<{ color?: string }>`
	color: ${({ color }) => color || "#fff"};
	text-align: right;
`;

const Hint = styled.div`
	margin-top: 2px;
	color: #888;
	font-size: 8px;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const Dump = styled.button`
	flex: 1;
	background: #2a2a2a;
	color: #ddd;
	border: 1px solid #444;
	border-radius: 4px;
	padding: 2px 4px;
	font-family: inherit;
	font-size: 8px;
	cursor: pointer;
	white-space: nowrap;
`;

const Dumps = styled.div`
	display: flex;
	gap: 3px;
	margin-top: 4px;
`;

const Chart = styled.canvas`
	width: 100%;
	height: 32px;
	margin-top: 4px;
	border: 1px solid #333;
	border-radius: 3px;
`;

const emptyPerf = (): FrontendPerfSnapshot => ({
	current: {
		t: 0,
		fromResponseMs: 0,
		fromResponseRuns: 0,
		fromResponseItems: 0,
		fromResponseHits: 0,
		fromResponseMisses: 0,
		stageMapMs: 0,
		wsEvents: 0,
		hydrates: 0,
		reduxOrders: 0,
		commits: 0,
		commitMs: 0,
		longTasks: 0,
		longTaskMs: 0,
		byId: {},
	},
	last: null,
	history: [],
	topCommits: [],
});

const DevToolbar: React.FC = () => {
	const [fps, setFps] = useState(0);
	const [avgFps, setAvgFps] = useState(0);
	const [domCount, setDomCount] = useState(0);
	const [heap, setHeap] = useState<number | null>(null);
	const [perf, setPerf] = useState<FrontendPerfSnapshot>(emptyPerf);

	const [dragging, setDragging] = useState(false);
	const [position, setPosition] = useState({
		x: 10,
		y: 10,
	});

	const wrapperRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<HTMLCanvasElement>(null);

	const frame = useRef(0);
	const last = useRef(performance.now());
	const fpsHistory = useRef<number[]>([]);
	const heapHistory = useRef<number[]>([]);

	const isLocalhost = DEV_HOSTS.includes(window.location.hostname);
	const isDev = process.env.NODE_ENV === "development";

	useEffect(() => {
		if (!isDev || !isLocalhost) return undefined;

		startFrontendPerfObservers();

		const updateDomCount = () => {
			setDomCount(document.getElementsByTagName("*").length);
		};

		const updateHeap = () => {
			if ((performance as any)?.memory) {
				const { usedJSHeapSize } = (performance as any).memory;
				const value = usedJSHeapSize / 1024 / 1024;
				setHeap(value);
				heapHistory.current.push(value);
				if (heapHistory.current.length > 20)
					heapHistory.current.shift();
			}
		};

		const updateFPS = () => {
			const now = performance.now();
			frame.current++;
			if (now >= last.current + 1000) {
				const currentFps = frame.current;
				setFps(currentFps);
				setPerf(getFrontendPerfSnapshot());

				fpsHistory.current.push(currentFps);
				if (fpsHistory.current.length > 20) fpsHistory.current.shift();

				const avg =
					fpsHistory.current.reduce((a, b) => a + b, 0) /
					fpsHistory.current.length;
				setAvgFps(Math.round(avg));

				drawChart(avg);

				frame.current = 0;
				last.current = now;
			}
			requestAnimationFrame(updateFPS);
		};

		updateDomCount();
		updateHeap();
		updateFPS();

		const interval = setInterval(() => {
			updateDomCount();
			updateHeap();
		}, 2000);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!dragging) return;
			const offsetX = 80;
			const offsetY = 16;
			setPosition({
				x: e.clientX - offsetX,
				y: window.innerHeight - e.clientY - offsetY,
			});
		};

		const handleMouseUp = () => setDragging(false);
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [dragging]);

	useEffect(() => {
		const handleResize = () => {
			setPosition((pos) => ({
				...pos,
				y: Math.min(pos.y, window.innerHeight - 80),
			}));
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const drawChart = useCallback((avg: number) => {
		const canvas = chartRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const { width, height } = canvas;

		ctx.clearRect(0, 0, width, height);
		ctx.fillStyle = "#1e1e1e";
		ctx.fillRect(0, 0, width, height);

		const allValues = [...fpsHistory.current, avg];
		const rawMax = Math.max(60, ...allValues);
		const paddedMax = Math.ceil((rawMax + 30) / 30) * 30;

		const zoneHeight = (v: number) => (v / paddedMax) * height;

		ctx.fillStyle = "rgba(244, 67, 54, 0.1)";
		ctx.fillRect(0, height - zoneHeight(30), width, zoneHeight(30));

		ctx.fillStyle = "rgba(255, 152, 0, 0.1)";
		ctx.fillRect(0, height - zoneHeight(50), width, zoneHeight(20));

		ctx.fillStyle = "rgba(76, 175, 80, 0.1)";
		ctx.fillRect(0, 0, width, height - zoneHeight(50));

		ctx.strokeStyle = "#444";
		ctx.lineWidth = 1;
		ctx.font = "8px monospace";
		ctx.fillStyle = "#888";

		for (let mark = 30; mark <= paddedMax; mark += 30) {
			const y = height - (mark / paddedMax) * height;
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(width, y);
			ctx.stroke();
			ctx.fillText(`${mark}`, 2, y - 2);
		}

		const step = width / (fpsHistory.current.length - 1);
		ctx.fillStyle = "#888";
		for (let i = 5; i < fpsHistory.current.length; i += 5) {
			const x = i * step;
			ctx.fillText(`${i}s`, x, height - 2);
			ctx.beginPath();
			ctx.moveTo(x, height);
			ctx.lineTo(x, height - 4);
			ctx.stroke();
		}

		const drawLine = (data: number[], color: string) => {
			if (data.length < 2) return;
			ctx.beginPath();
			for (let i = 0; i < data.length; i++) {
				const x = i * step;
				const y = height - (data[i] / paddedMax) * height;
				i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
			}
			ctx.strokeStyle = color;
			ctx.lineWidth = 2;
			ctx.stroke();
		};

		drawLine(fpsHistory.current, "rgba(76, 175, 80, 0.85)");
		const avgArray = new Array(fpsHistory.current.length).fill(avg);
		drawLine(avgArray, "rgba(255, 235, 59, 0.85)");
	}, []);

	const getFpsColor = useCallback((value: number) => {
		if (value > 50) return "#4caf50";
		if (value > 30) return "#ff9800";
		return "#f44336";
	}, []);

	const getLoadColor = useCallback((bad: boolean, warn: boolean) => {
		if (bad) return "#f44336";
		if (warn) return "#ff9800";
		return "#4caf50";
	}, []);

	if (!isDev || !isLocalhost) return null;

	const bucket = perf.last ?? perf.current;
	const missRate =
		bucket.fromResponseHits + bucket.fromResponseMisses
			? Math.round(
					(bucket.fromResponseMisses * 100) /
						(bucket.fromResponseHits + bucket.fromResponseMisses),
			  )
			: 0;

	return (
		<Wrapper
			ref={wrapperRef}
			$dragging={dragging}
			style={{ left: position.x, bottom: position.y }}
			onMouseDown={() => setDragging(true)}
		>
			<Grid>
				<Row>
					<span>DOM</span>
					<Value>{domCount}</Value>
				</Row>
				<Row>
					<span>FPS</span>
					<Value color={getFpsColor(fps)}>{fps}</Value>
				</Row>
				<Row>
					<span>Avg</span>
					<Value color={getFpsColor(avgFps)}>{avgFps}</Value>
				</Row>
				<Row>
					<span>Heap</span>
					<Value>
						{heap !== null ? `${heap.toFixed(0)}MB` : "N/A"}
					</Value>
				</Row>
				<Row>
					<span>commits</span>
					<Value
						color={getLoadColor(
							bucket.commits > 30,
							bucket.commits > 12,
						)}
					>
						{bucket.commits}/{Math.round(bucket.commitMs)}ms
					</Value>
				</Row>
				<Row>
					<span>long</span>
					<Value
						color={getLoadColor(
							bucket.longTasks > 2,
							bucket.longTasks > 0,
						)}
					>
						{bucket.longTasks}/{Math.round(bucket.longTaskMs)}ms
					</Value>
				</Row>
				<Row>
					<span>fromRes</span>
					<Value
						color={getLoadColor(
							bucket.fromResponseMs > 50 || missRate > 40,
							bucket.fromResponseMs > 16 || missRate > 15,
						)}
					>
						{Math.round(bucket.fromResponseMs)}ms {missRate}%
					</Value>
				</Row>
				<Row>
					<span>stage</span>
					<Value>{Math.round(bucket.stageMapMs)}ms</Value>
				</Row>
				<Row>
					<span>WS</span>
					<Value
						color={getLoadColor(
							bucket.wsEvents > 40,
							bucket.wsEvents > 15,
						)}
					>
						{bucket.wsEvents}
					</Value>
				</Row>
				<Row>
					<span>hyd/rdx</span>
					<Value>
						{bucket.hydrates}/{bucket.reduxOrders}
					</Value>
				</Row>
				{perf.topCommits.map((item) => (
					<Row key={item.id}>
						<span>#{item.id}</span>
						<Value>
							{item.n}×{Math.round(item.ms)}ms
						</Value>
					</Row>
				))}
			</Grid>
			<Chart ref={chartRef} width={232} height={32} />
			<Hint title="commits = React tree rerenders. miss% = orders without cell cache. dump = last 60s table in console.">
				commits = rerenders · miss% = no cell cache · dump = console
			</Hint>
			<Dumps>
				<Dump
					type="button"
					onMouseDown={(event) => event.stopPropagation()}
					title="dump 60s → console"
					onClick={() => dumpFrontendPerf()}
				>
					dump
				</Dump>
				<Dump
					type="button"
					title="probe /orders 2k → console"
					onMouseDown={(event) => event.stopPropagation()}
					onClick={() =>
						dumpOrdersRenderProbe(runOrdersRenderProbe(2000))
					}
				>
					probe
				</Dump>
				<Dump
					type="button"
					title="stress /orders 2k×100 → console"
					onMouseDown={(event) => event.stopPropagation()}
					onClick={() =>
						dumpOrdersPerfStress(runOrdersPerfStress(2000, 100))
					}
				>
					stress
				</Dump>
			</Dumps>
		</Wrapper>
	);
};

export default DevToolbar;
